import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Shield, Users, CheckCircle, Video, Lock, Factory,
  Building2, Home as HomeIcon, Medal, Star, Target, Eye
} from 'lucide-react';

import slider1 from '../assets/slider1.jpeg';
import slider2 from '../assets/slider2.jpeg';
import slider3 from '../assets/slider3.jpg';
import slider4 from '../assets/slider4.jpeg';
import alider5 from '../assets/alider5.jpeg'; // keeping original filename
import directorImg from '../assets/director.jpeg';
import gallery1 from '../assets/gallery1.jpeg';
import gallery2 from '../assets/gallery2.jpg';
import gallery3 from '../assets/gallery3.jpeg';
import gallery4 from '../assets/gallery4.jpeg';
import gallery5 from '../assets/gallery5.jpeg';
import gallery6 from '../assets/gallery6.jpeg';
import gallery7 from '../assets/gallery7.jpeg';
import gallery8 from '../assets/gallery8.png';
import gallery9 from '../assets/gallery9.jpeg';
import video1 from '../assets/video1.mp4';
import video2 from '../assets/video2.mp4';
import video3 from '../assets/video3.mp4';

const galleryImages = [gallery1, gallery2, gallery3, gallery4, gallery5, gallery6, gallery7, gallery8, gallery9];
const videoFiles = [video1, video2, video3];

// --- Sub-components ---

const Section = ({ children, className = "" }: { children: React.ReactNode, className?: string }) => (
  <section className={`py-16 md:py-24 px-4 overflow-hidden ${className}`}>
    <div className="container mx-auto">
      {children}
    </div>
  </section>
);

const SectionTitle = ({ title, subtitle, centered = true }: { title: string, subtitle?: string, centered?: boolean }) => (
  <div className={`mb-12 ${centered ? 'text-center' : 'text-left'}`}>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="inline-block"
    >
      <h2 className="text-3xl md:text-5xl font-bold mb-4 gradient-text">{title}</h2>
      <div className={`h-1.5 w-24 bg-primary-500 rounded-full ${centered ? 'mx-auto' : ''}`}></div>
    </motion.div>
    {subtitle && (
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
        className="mt-4 text-lg text-secondary-500 dark:text-secondary-400 max-w-2xl mx-auto"
      >
        {subtitle}
      </motion.p>
    )}
  </div>
);

// --- Sections ---

const Hero = () => (
  <div className="relative h-screen flex items-center justify-center overflow-hidden">
    {/* Background Image with Overlay */}
    <div className="absolute inset-0 z-0">
      <img src={slider1} alt="Security" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-secondary-900/90 to-secondary-900/60 mix-blend-multiply"></div>
      <div className="absolute inset-0 bg-black/40"></div>
    </div>

    {/* Content */}
    <div className="container mx-auto px-4 z-10 text-center text-white">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
          <Shield className="text-primary-400 w-5 h-5" />
          <span className="text-sm font-medium tracking-wide uppercase">Devdoot Intelligence & Securities</span>
        </div>
        <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
          Security <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-accent">Reimagined</span>
        </h1>
        <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto mb-10 font-light">
          Providing integrated elite security solutions with a blend of armed forces precision and modern technology.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button className="px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold text-lg transition-all shadow-lg hover:shadow-primary-500/25 flex items-center justify-center gap-2 group">
            Our Services
            <Target size={20} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button className="px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white border border-white/20 rounded-lg font-semibold text-lg transition-all flex items-center justify-center gap-2">
            Contact Us
          </button>
        </div>
      </motion.div>
    </div>

    {/* Scroll Indicator */}
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1, duration: 1 }}
      className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/50 animate-bounce"
    >
      <div className="w-8 h-12 border-2 border-white/30 rounded-full flex justify-center p-2">
        <div className="w-1 h-3 bg-white rounded-full"></div>
      </div>
    </motion.div>
  </div>
);

const Stats = () => (
  <div className="bg-primary-600 py-12">
    <div className="container mx-auto px-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-white text-center">
        {[
          { label: 'Years Experience', value: '20+' },
          { label: 'Security Personnel', value: '500+' },
          { label: 'Happy Clients', value: '100%' },
          { label: 'Districts Covered', value: '38+' }
        ].map((stat, i) => (
          <div key={i} className="p-4">
            <div className="text-4xl md:text-5xl font-bold mb-2">{stat.value}</div>
            <div className="text-primary-100 font-medium">{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const DirectorDesk = () => (
  <Section className="bg-secondary-50 dark:bg-secondary-800/50">
    <div className="flex flex-col lg:flex-row gap-12 items-center">
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="w-full lg:w-1/3"
      >
        <div className="relative group">
          <div className="absolute inset-0 bg-primary-500 rounded-2xl rotate-3 group-hover:rotate-6 transition-transform opacity-20"></div>
          <img
            src={directorImg}
            alt="Director"
            className="rounded-2xl shadow-2xl w-full max-w-md mx-auto relative z-10"
          />
          <div className="absolute bottom-6 left-6 right-6 p-4 bg-white/90 dark:bg-secondary-900/90 backdrop-blur-md rounded-xl shadow-lg z-20 border border-white/20">
            <h3 className="font-bold text-lg text-secondary-900 dark:text-white">Rahul Kumar Singh</h3>
            <p className="text-primary-600 dark:text-primary-400 font-medium">Managing Director</p>
          </div>
        </div>
      </motion.div>
      <div className="w-full lg:w-2/3">
        <SectionTitle title="Director's Desk" subtitle="A message from our leadership" centered={false} />
        <div className="space-y-6 text-lg text-secondary-600 dark:text-secondary-300 leading-relaxed">
          <p>
            "We are a unique blend of self-driven, young, and energetic team working under the able guidance of highly experienced security experts. Our primary focus is delivering the best and most accurate results with an emphasis on customer satisfaction."
          </p>
          <p>
            "The setup of the company comprises thorough professionals drawn from the Indian Armed Forces and semi-armed forces. We assure you that the standard and quality of our services will meet your requirement and expectation."
          </p>
          <div className="p-6 bg-white dark:bg-secondary-900 rounded-xl border border-secondary-200 dark:border-secondary-700 mt-8">
            <h4 className="font-bold text-xl mb-4 flex items-center gap-2">
              <Users className="text-primary-500" />
              Our Personnel
            </h4>
            <p className="text-base">
              Professionally trained, knowledgeable, dedicated, courageous, and vigilant — capable of handling challenging situations. We are fully equipped with high-tech security systems and the latest technology.
            </p>
          </div>
        </div>
      </div>
    </div>
  </Section>
);

const Services = () => (
  <Section>
    <SectionTitle title="Our Expertise" subtitle="Comprehensive security solutions tailored to your needs" />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {[
        { title: 'Industrial Security', icon: Factory, desc: 'Full proof static activities, threat assessment, and "security in depth" for industries.', color: 'text-blue-500' },
        { title: 'Corporate Security', icon: Building2, desc: 'Specialized gadget installation, CCTV monitoring, and access control for corporate houses.', color: 'text-green-500' },
        { title: 'Residential Security', icon: HomeIcon, desc: 'Reliable guards with thorough background checks to ensure safety of your loved ones.', color: 'text-yellow-500' },
        { title: 'Escort Services', icon: Shield, desc: 'Armed and unarmed escort services available 24/7 with professional security officers.', color: 'text-red-500' },
        { title: 'Security Consultancy', icon: Eye, desc: 'Vulnerability assessment, risk management, and end-to-end security solution design.', color: 'text-purple-500' },
        { title: 'Training Centre', icon: Star, desc: 'Modern training facility equipping personnel with skills for 21 days as per PSARA guidelines.', color: 'text-cyan-500' }
      ].map((service, idx) => (
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: idx * 0.1 }}
          whileHover={{ y: -10 }}
          className="bg-white dark:bg-secondary-800 p-8 rounded-2xl shadow-lg border border-secondary-100 dark:border-secondary-700 hover:shadow-xl hover:shadow-primary-500/10 transition-all group"
        >
          <div className={`p-4 rounded-full bg-secondary-50 dark:bg-secondary-900 w-fit mb-6 group-hover:scale-110 transition-transform`}>
            <service.icon size={32} className={service.color} />
          </div>
          <h3 className="text-xl font-bold mb-3 text-secondary-900 dark:text-white group-hover:text-primary-500 transition-colors">{service.title}</h3>
          <p className="text-secondary-500 dark:text-secondary-400 leading-relaxed">
            {service.desc}
          </p>
        </motion.div>
      ))}
    </div>
  </Section>
);

const WhyChooseUs = () => (
  <Section className="bg-secondary-900 text-white relative overflow-hidden">
    {/* Decorative blobs */}
    <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500/20 blur-3xl rounded-full translate-x-1/2 -translate-y-1/2"></div>
    <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/20 blur-3xl rounded-full -translate-x-1/2 translate-y-1/2"></div>

    <div className="relative z-10">
      <SectionTitle title="Core Values" subtitle="What defines Devdoot Intelligence" centered />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mt-16">
        {[
          { icon: Target, title: 'Mission', desc: 'To provide the highest quality of security & safety services reflecting effective potential and loss prevention.' },
          { icon: Eye, title: 'Vision', desc: 'To extend best protection focusing on specific needs and maintaining a culture of excellence and innovation.' },
          { icon: Medal, title: 'Quality', desc: 'A financially responsible organization staffed by professionals capable of delivering specific management skills.' }
        ].map((item, i) => (
          <div key={i} className="text-center group">
            <div className="w-20 h-20 mx-auto bg-white/10 rounded-2xl flex items-center justify-center mb-6 backdrop-blur-sm group-hover:bg-primary-500 transition-colors">
              <item.icon size={40} className="text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
            <p className="text-gray-300 leading-relaxed">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

const Clients = () => (
  <Section className="bg-white dark:bg-secondary-900">
    <SectionTitle title="Our Clients" subtitle="Sectors we proudly serve" />
    <div className="flex flex-wrap justify-center gap-6">
      {[
        'Banks', 'Corporate Houses', 'Hospitals', 'Residential Properties',
        'Shopping Malls', 'Hotels', 'Financial Institutions', 'Industries', 'Govt Organization'
      ].map((client, i) => (
        <motion.div
          key={i}
          whileHover={{ scale: 1.05 }}
          className="px-6 py-3 rounded-full border border-secondary-200 dark:border-secondary-700 text-secondary-600 dark:text-secondary-300 font-medium hover:border-primary-500 hover:text-primary-500 transition-colors cursor-default"
        >
          {client}
        </motion.div>
      ))}
    </div>
  </Section>
);

const Gallery = () => (
  <Section className="bg-secondary-50 dark:bg-secondary-800/30">
    <SectionTitle title="Gallery" subtitle="Glimpses of our operations and team" />

    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-[200px]">
      {galleryImages.slice(0, 8).map((img, i) => (
        <motion.div
          key={i}
          whileHover={{ scale: 1.02 }}
          className={`rounded-xl overflow-hidden shadow-md cursor-pointer relative group ${i === 0 || i === 5 ? 'col-span-2 row-span-2' : ''}`}
        >
          <img src={img} alt={`Gallery ${i}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Eye className="text-white w-8 h-8" />
          </div>
        </motion.div>
      ))}
    </div>
  </Section>
);

const VideoGallery = () => (
  <Section>
    <SectionTitle title="Video Tour" />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {videoFiles.map((vid, i) => (
        <div key={i} className="rounded-xl overflow-hidden shadow-lg bg-black">
          <video controls className="w-full h-48 md:h-64 object-cover">
            <source src={vid} type="video/mp4" />
          </video>
        </div>
      ))}
    </div>
  </Section>
);

const ContactForm = () => {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const phone = '9852891115';
    const text = encodeURIComponent(`Name: ${name}\nMessage: ${message}`);
    const url = `https://wa.me/${phone}?text=${text}`;
    window.open(url, '_blank');
    setName('');
    setMessage('');
  };

  return (
    <Section className="bg-primary-600 text-white relative">
      <div className="absolute inset-0 pattern-dots opacity-10"></div>
      <div className="flex flex-col md:flex-row gap-12 items-center relative z-10">
        <div className="w-full md:w-1/2">
          <h2 className="text-4xl font-bold mb-6">Ready to secure your future?</h2>
          <p className="text-xl text-primary-100 mb-8">
            Contact us today for a free consultation. Our experts are ready to design a custom security plan for you.
          </p>
          <ul className="space-y-4">
            <li className="flex items-center gap-3"><CheckCircle className="text-primary-200" /> 24/7 Support</li>
            <li className="flex items-center gap-3"><CheckCircle className="text-primary-200" /> Professional Team</li>
            <li className="flex items-center gap-3"><CheckCircle className="text-primary-200" /> Custom Solutions</li>
          </ul>
        </div>

        <div className="w-full md:w-1/2 bg-white text-secondary-900 rounded-2xl p-8 shadow-2xl">
          <h3 className="text-2xl font-bold mb-6">Instant WhatsApp Inquiry</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-secondary-600 mb-1">Your Name</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-secondary-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                placeholder="Enter your name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-600 mb-1">How can we help?</label>
              <textarea
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 rounded-lg border border-secondary-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                placeholder="Tell us about your requirements"
              />
            </div>
            <button type="submit" className="w-full py-3 bg-[#25D366] hover:bg-[#128C7E] text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2">
              <span>Chat on WhatsApp</span>
            </button>
          </form>
        </div>
      </div>
    </Section>
  )
}

const Home = () => {
  return (
    <div className="overflow-x-hidden">
      <Hero />
      <Stats />
      <DirectorDesk />
      <Services />
      <WhyChooseUs />
      <Clients />
      <Gallery />
      <VideoGallery />
      <ContactForm />

      {/* Map Section */}
      <div className="h-96 w-full grayscale hover:grayscale-0 transition-all duration-700">
        <iframe
          title="DIAS Office Location"
          width="100%"
          height="100%"
          frameBorder="0"
          style={{ border: 0 }}
          src="https://www.google.com/maps?q=25.5900105,85.1622441&z=17&hl=en&output=embed"
          allowFullScreen
        ></iframe>
      </div>
    </div>
  );
};

export default Home;